// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 07-source-control-pipeline.md §Re-install semantics
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 20-agent-plugins.md#marketplace-repo-bootstrap
import * as React from 'react';
import { ArrowRight, ArrowUpRight, CheckCircle2, XCircle } from 'lucide-react';

import { SectionCard } from '@/components/greenlight/SectionCard';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { ScmMigrationPreview, ScmMigrationResult } from '@greenlight/shared';

/** Result of a preview/execute call — ok with a body, or a surfaced server error. */
export type MigrationPreviewResult =
  | { ok: true; preview: ScmMigrationPreview }
  | { ok: false; code?: string; message: string };
export type MigrationExecuteResult =
  | { ok: true; result: ScmMigrationResult }
  | { ok: false; code?: string; message: string };

export interface SourceControlMigrationProps {
  /** Dry-run the migration (POST …/migration/preview). */
  onPreview: () => Promise<MigrationPreviewResult>;
  /** Execute after the manual repo transfer (POST …/migration/execute). */
  onExecute: (targetOrgHandle: string) => Promise<MigrationExecuteResult>;
  className?: string;
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'previewing' }
  | { kind: 'no_target'; message: string }
  | { kind: 'error'; message: string }
  | { kind: 'preview'; preview: ScmMigrationPreview }
  | { kind: 'executing'; preview: ScmMigrationPreview }
  | { kind: 'done'; result: ScmMigrationResult };

export function SourceControlMigration({
  onPreview,
  onExecute,
  className,
}: SourceControlMigrationProps): React.JSX.Element {
  const [phase, setPhase] = React.useState<Phase>({ kind: 'idle' });
  const [confirm, setConfirm] = React.useState('');
  const [execError, setExecError] = React.useState<string | null>(null);
  // Monotonic guard: only the latest preview/execute may write phase, so rapid
  // re-checks (or a preview landing after an execute) can't clobber newer state.
  const seqRef = React.useRef(0);

  async function check(): Promise<void> {
    const seq = ++seqRef.current;
    setPhase({ kind: 'previewing' });
    setConfirm('');
    setExecError(null);
    const res = await onPreview();
    if (seq !== seqRef.current) return; // a newer check/execute superseded this one
    if (res.ok) {
      setPhase({ kind: 'preview', preview: res.preview });
    } else if (res.code === 'scm.migration_target_missing') {
      setPhase({ kind: 'no_target', message: res.message });
    } else {
      setPhase({ kind: 'error', message: res.message });
    }
  }

  async function execute(preview: ScmMigrationPreview): Promise<void> {
    const seq = ++seqRef.current;
    setExecError(null);
    setPhase({ kind: 'executing', preview });
    const res = await onExecute(preview.target.org_handle);
    if (seq !== seqRef.current) return; // superseded (e.g. operator re-checked)
    if (res.ok) {
      setPhase({ kind: 'done', result: res.result });
    } else {
      // Stay on the preview so the operator can retry after fixing the transfer.
      setExecError(res.message);
      setPhase({ kind: 'preview', preview });
    }
  }

  return (
    <SectionCard headerBar title="Migrate to a different GitHub organization" className={className}>
      <div className="space-y-4 px-5 py-4" data-testid="scm-migration">
        <p className="text-sm text-muted-foreground">
          Move the Greenlight GitHub App and every managed repo to a different GitHub organization.
          Apps stay deployed throughout — only their source-control coordinates change.
        </p>

        {phase.kind === 'done' ? (
          <MigrationResult result={phase.result} onReset={() => setPhase({ kind: 'idle' })} />
        ) : phase.kind === 'preview' || phase.kind === 'executing' ? (
          <MigrationPlan
            preview={phase.preview}
            confirm={confirm}
            onConfirmChange={setConfirm}
            executing={phase.kind === 'executing'}
            execError={execError}
            onExecute={() => void execute(phase.preview)}
            onRecheck={() => void check()}
          />
        ) : phase.kind === 'no_target' ? (
          <NoTargetGuidance message={phase.message} onRecheck={() => void check()} />
        ) : (
          <div className="space-y-3">
            {phase.kind === 'error' ? (
              <Alert variant="destructive" data-testid="scm-migration-error">
                <AlertTitle>Couldn&apos;t check migration status</AlertTitle>
                <AlertDescription>{phase.message}</AlertDescription>
              </Alert>
            ) : null}
            <Button
              type="button"
              variant="outline"
              onClick={() => void check()}
              disabled={phase.kind === 'previewing'}
              className="h-11 md:h-9"
              data-testid="scm-migration-check"
            >
              {phase.kind === 'previewing' ? 'Checking…' : 'Check migration status'}
            </Button>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

// ── No pending target ─────────────────────────────────────────────────────────

function NoTargetGuidance({
  message,
  onRecheck,
}: {
  message: string;
  onRecheck: () => void;
}): React.JSX.Element {
  return (
    <div className="space-y-3" data-testid="scm-migration-no-target">
      <Alert>
        <AlertTitle>No destination organization detected</AlertTitle>
        <AlertDescription>{message}</AlertDescription>
      </Alert>
      <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
        <li>
          Install the Greenlight GitHub App on the destination organization — its installation
          webhook stages the migration here.
        </li>
        <li>Come back and re-check; the planned repo moves will appear.</li>
      </ol>
      <div className="flex flex-col gap-2 sm:flex-row">
        <a
          href="https://github.com/apps"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground h-11 md:h-9"
        >
          Manage on GitHub
          <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
        </a>
        <Button
          type="button"
          variant="outline"
          onClick={onRecheck}
          className="h-11 md:h-9"
          data-testid="scm-migration-recheck"
        >
          Re-check
        </Button>
      </div>
    </div>
  );
}

// ── Pending target → plan + confirm ───────────────────────────────────────────

function MigrationPlan({
  preview,
  confirm,
  onConfirmChange,
  executing,
  execError,
  onExecute,
  onRecheck,
}: {
  preview: ScmMigrationPreview;
  confirm: string;
  onConfirmChange: (v: string) => void;
  executing: boolean;
  execError: string | null;
  onExecute: () => void;
  onRecheck: () => void;
}): React.JSX.Element {
  const target = preview.target.org_handle;
  const confirmed = confirm.trim() === target;
  return (
    <div className="space-y-4" data-testid="scm-migration-plan">
      {/* current → target */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <MonoValue title={preview.current.org_handle}>{preview.current.org_handle}</MonoValue>
        <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <MonoValue title={target}>{target}</MonoValue>
      </div>

      {/* repo moves */}
      <div className="rounded-gl-lg border border-border overflow-hidden">
        <p className="border-b border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
          {preview.repos.length} {preview.repos.length === 1 ? 'repo' : 'repos'} will be re-pointed
        </p>
        <ul className="divide-y divide-border">
          {preview.repos.map((r) => (
            <li
              key={`${r.resource_kind}:${r.resource_id}`}
              className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <span className="flex items-center gap-2 text-sm text-foreground">
                {r.resource_name}
                {r.resource_kind === 'plugin_marketplace' ? (
                  <span className="rounded border border-border px-1.5 py-0.5 text-[0.68rem] text-muted-foreground">
                    Agent plugin repo
                  </span>
                ) : null}
              </span>
              <span className="flex flex-wrap items-center gap-1.5">
                <MonoValue className="max-w-[160px] truncate" title={r.from}>
                  {r.from}
                </MonoValue>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                <MonoValue className="max-w-[160px] truncate" title={r.to}>
                  {r.to}
                </MonoValue>
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* manual transfer checklist */}
      <Alert>
        <AlertTitle>Transfer the repos in GitHub first</AlertTitle>
        <AlertDescription>
          GitHub Apps can&apos;t move repos between organizations. In the GitHub UI, transfer each
          repo above to <span className="font-mono">{target}</span>, then run the migration — it
          verifies every repo is reachable under the new organization before re-pointing anything.
        </AlertDescription>
      </Alert>

      {execError ? (
        <Alert variant="destructive" data-testid="scm-migration-exec-error">
          <AlertTitle>Migration didn&apos;t run</AlertTitle>
          <AlertDescription>{execError}</AlertDescription>
        </Alert>
      ) : null}

      {/* typed confirmation */}
      <div className="space-y-2">
        <Label htmlFor="scm-migration-confirm">
          Type <span className="font-mono">{target}</span> to confirm
        </Label>
        <Input
          id="scm-migration-confirm"
          value={confirm}
          onChange={(e) => onConfirmChange(e.target.value)}
          autoComplete="off"
          placeholder={target}
          className="h-11 md:h-9"
          data-testid="scm-migration-confirm"
        />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          type="button"
          onClick={onExecute}
          disabled={!confirmed || executing}
          className="h-11 md:h-9"
          data-testid="scm-migration-execute"
        >
          {executing ? 'Migrating…' : 'Execute migration'}
        </Button>
        {/* Re-check picks up a target that changed during the manual transfer
            (e.g. the migration was cancelled by uninstalling on the target org). */}
        <Button
          type="button"
          variant="outline"
          onClick={onRecheck}
          disabled={executing}
          className="h-11 md:h-9"
          data-testid="scm-migration-plan-recheck"
        >
          Re-check
        </Button>
      </div>
    </div>
  );
}

// ── Result ────────────────────────────────────────────────────────────────────

function MigrationResult({
  result,
  onReset,
}: {
  result: ScmMigrationResult;
  onReset: () => void;
}): React.JSX.Element {
  return (
    <div className="space-y-4" data-testid="scm-migration-result">
      <Alert>
        <AlertTitle>
          Migrated to {result.org_handle} · {result.repos_moved}{' '}
          {result.repos_moved === 1 ? 'repo' : 'repos'} re-pointed
        </AlertTitle>
        <AlertDescription>
          Apps stayed deployed throughout — only source-control coordinates changed.
        </AlertDescription>
      </Alert>

      <ul className="divide-y divide-border rounded-gl-lg border border-border overflow-hidden">
        {result.post_checks.map((c) => (
          <li key={c.repo_full_name} className="flex flex-col gap-1 px-4 py-3">
            <MonoValue className="max-w-full truncate" title={c.repo_full_name}>
              {c.repo_full_name}
            </MonoValue>
            {c.error ? (
              <span className="flex items-center gap-1.5 text-xs text-destructive">
                <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                {c.error}
              </span>
            ) : (
              <span className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                <PostCheckFlag ok={c.branch_protection_applied} label="branch protection" />
                <PostCheckFlag ok={c.workflow_present} label="pipeline workflow" />
              </span>
            )}
          </li>
        ))}
      </ul>

      <Button
        type="button"
        variant="outline"
        onClick={onReset}
        className="h-11 md:h-9"
        data-testid="scm-migration-reset"
      >
        Done
      </Button>
    </div>
  );
}

function PostCheckFlag({ ok, label }: { ok: boolean; label: string }): React.JSX.Element {
  return (
    <span className={cn('flex items-center gap-1', ok ? 'text-gl-green-text' : 'text-destructive')}>
      {ok ? (
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {label}
    </span>
  );
}
